import React, { useId } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FiAlertTriangle, FiSend } from "react-icons/fi";
import {
  canRequestActivityReview,
  isCorrectionWorkflowStatus,
} from "@core/reports/utils/reportWorkflow";

// react-bootstrap's OverlayTrigger mis-types children under React 19.
const OverlayTriggerRoot = OverlayTrigger as unknown as React.FC<
  React.PropsWithChildren<Record<string, unknown>>
>;

export const TEMPLATE_REVIEW_HELP_TEXT =
  "Review requested. This template is in Needs Attention.";

export const TEMPLATE_REQUEST_REVIEW_LABEL = "Request review for this template";

const SLOT_STYLE: React.CSSProperties = {
  width: 28,
  height: 28,
  border: "1px solid var(--ntcog-border)",
  borderRadius: "var(--ntcog-radius-sm)",
  background: "var(--ntcog-input)",
  cursor: "pointer",
  lineHeight: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flex: "0 0 auto",
  marginTop: 2,
};

export type TemplateRowReviewActionProps = {
  activityStatus?: string | null;
  parentDepartmentStatus?: string | null;
  canReviewActivity: boolean;
  permissionsLoading: boolean;
  busy?: boolean;
  disabled?: boolean;
  onRequestReview: () => void;
};

export default function TemplateRowReviewAction({
  activityStatus,
  parentDepartmentStatus,
  canReviewActivity,
  permissionsLoading,
  busy = false,
  disabled = false,
  onRequestReview,
}: TemplateRowReviewActionProps) {
  const tooltipId = useId().replace(/:/g, "");

  if (isCorrectionWorkflowStatus(activityStatus)) {
    return (
      <OverlayTriggerRoot
        placement="top"
        overlay={
          <Tooltip id={`template-review-caution-${tooltipId}`} aria-hidden="true">
            {TEMPLATE_REVIEW_HELP_TEXT}
          </Tooltip>
        }
      >
        <span
          className="text-warning"
          role="img"
          aria-label={TEMPLATE_REVIEW_HELP_TEXT}
          style={{
            ...SLOT_STYLE,
            border: "none",
            background: "transparent",
            cursor: "help",
          }}
        >
          <FiAlertTriangle aria-hidden />
        </span>
      </OverlayTriggerRoot>
    );
  }

  const showPlane =
    !permissionsLoading &&
    canReviewActivity &&
    canRequestActivityReview(activityStatus, parentDepartmentStatus);

  if (!showPlane) {
    return null;
  }

  return (
    <button
      type="button"
      aria-label={
        disabled ? "Missing template id for this row" : TEMPLATE_REQUEST_REVIEW_LABEL
      }
      disabled={disabled || busy}
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onRequestReview();
      }}
      style={{
        ...SLOT_STYLE,
        background: disabled || busy ? "var(--ntcog-surface)" : "var(--ntcog-input)",
        cursor: disabled || busy ? "not-allowed" : "pointer",
      }}
    >
      <FiSend aria-hidden />
    </button>
  );
}
